import type { FC } from 'react';
import { Button, Card, Flex, Input, Typography } from 'antd';
import {
  BankOutlined,
  EyeOutlined,
  EyeInvisibleOutlined,
  LeftOutlined,
  RightOutlined,
  SearchOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { PortfolioPropertyRowViewModel } from '../../PortfolioPage.types';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';

const { Text, Title } = Typography;

export interface PropertiesTableCardProps {
  title: string;
  searchPlaceholder: string;
  searchValue: string;
  onSearchChange: (value: string) => void;
  attentionFilterLabel: string;
  attentionFilterActive: boolean;
  onAttentionFilterChange: (active: boolean) => void;
  columns: ColumnsType<PortfolioPropertyRowViewModel>;
  rows: PortfolioPropertyRowViewModel[];
  alertMessage: string | null;
  showingLabel: string;
  pageLabel: string;
  prevPageAriaLabel: string;
  nextPageAriaLabel: string;
  page: number;
  pageCount: number;
  onPageChange: (page: number) => void;
}

export const PropertiesTableCard: FC<PropertiesTableCardProps> = ({
  title,
  searchPlaceholder,
  searchValue,
  onSearchChange,
  attentionFilterLabel,
  attentionFilterActive,
  onAttentionFilterChange,
  columns,
  rows,
  alertMessage,
  showingLabel,
  pageLabel,
  prevPageAriaLabel,
  nextPageAriaLabel,
  page,
  pageCount,
  onPageChange,
}) => {
  return (
    <Card className="om-section-card" styles={{ body: { padding: 24 } }}>
      <Flex vertical gap={16}>
        <Flex align="center" gap={8}>
          <BankOutlined style={{ color: 'var(--evimo-text-secondary)' }} />
          <Title level={5} className="ds-section-header" style={{ margin: 0 }}>
            {title}
          </Title>
        </Flex>

        <Flex gap={12} wrap="wrap" align="center">
          <Input
            allowClear
            value={searchValue}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder={searchPlaceholder}
            prefix={<SearchOutlined />}
            style={{ flex: 1, minWidth: 220 }}
          />
          <Button
            type={attentionFilterActive ? 'primary' : 'text'}
            icon={
              attentionFilterActive ? <EyeInvisibleOutlined /> : <EyeOutlined />
            }
            onClick={() => onAttentionFilterChange(!attentionFilterActive)}
          >
            {attentionFilterLabel}
          </Button>
        </Flex>

        <EvimoTable
          className="ds-table om-portfolio-table"
          columns={columns}
          dataSource={rows}
          rowKey="id"
          pagination={false}
          size="middle"
        />

        {alertMessage ? (
          <Flex align="flex-start" gap={10} className="om-portfolio-alert">
            <WarningOutlined className="om-portfolio-alert__icon" aria-hidden />
            <Text className="om-portfolio-alert__text">{alertMessage}</Text>
          </Flex>
        ) : null}

        <Flex justify="space-between" align="center" wrap="wrap" gap={12}>
          <Text type="secondary">{showingLabel}</Text>
          <Flex align="center" gap={8}>
            <Button
              type="text"
              size="small"
              icon={<LeftOutlined />}
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
              aria-label={prevPageAriaLabel}
            />
            <Text type="secondary">{pageLabel}</Text>
            <Button
              type="text"
              size="small"
              icon={<RightOutlined />}
              disabled={page >= pageCount}
              onClick={() => onPageChange(page + 1)}
              aria-label={nextPageAriaLabel}
            />
          </Flex>
        </Flex>
      </Flex>
    </Card>
  );
};
